import React from 'react';
import { CTASection } from '../../components/CTASection';
import { Sparkles, CheckCircle, Zap, Award, Layers, Target } from 'lucide-react';
export function LaserEtching() {
  const benefits = [{
    icon: <Target className="w-6 h-6" data-id="element-1718" />,
    title: 'Precision Engineering',
    description: 'Intricate designs with microscopic accuracy and clean edges'
  }, {
    icon: <Award className="w-6 h-6" data-id="element-1719" />,
    title: 'Durable & Lasting',
    description: 'Permanent etching that withstands wear, weather, and time'
  }, {
    icon: <Layers className="w-6 h-6" data-id="element-1720" />,
    title: 'Versatile Materials',
    description: 'Works on leather, apparel, fleece, boat covers, and more'
  }, {
    icon: <Sparkles className="w-6 h-6" data-id="element-1721" />,
    title: 'Custom Artistry',
    description: 'Transform any artwork or design into a one-of-a-kind piece'
  }];
  const materials = [{
    title: 'Leather',
    description: 'Create sleek, custom designs on wallets, belts, bags, and accessories',
    icon: '👜'
  }, {
    title: 'Apparel',
    description: 'Add intricate laser-cut designs to denim, fleece, performance gear, and more',
    icon: '👕'
  }, {
    title: 'Custom Art',
    description: 'Bring any artwork or design to life on various materials for unique pieces',
    icon: '🎨'
  }, {
    title: 'Specialty Items',
    description: 'Boat covers, outdoor gear, and other durable materials requiring precision',
    icon: '⛵'
  }];
  const applications = ['Intricate appliqués', 'Sleek engravings', 'Custom logos', 'Unique patterns', 'Personalized text', 'Detailed artwork', 'Brand marks', 'Decorative designs'];
  return <div className="flex flex-col w-full" data-id="element-1722">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-wh-teal to-teal-600 py-20" data-id="element-1723">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1724">
          <div className="flex justify-center mb-6" data-id="element-1725">
            <Sparkles className="w-16 h-16 text-white" data-id="element-1726" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-1727">
            Laser Etching
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto" data-id="element-1728">
            Precision meets creativity. Transform your ideas into stunning,
            permanent designs that leave a lasting impression—one precise etch
            at a time.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white" data-id="element-1729">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1730">
          <div className="max-w-4xl mx-auto mb-16" data-id="element-1731">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-1732">
              Engraving Your Imagination
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4" data-id="element-1733">
              <p data-id="element-1734">
                Since 2009, our trusted Seit Laser Bridge has opened up a whole
                new world of possibilities at Wear Haus Designs. With advanced
                laser etching technology, we transform your ideas into stunning
                designs that leave a lasting impression—whether it's intricate
                appliqués, sleek engravings, or custom artwork.
              </p>
              <p data-id="element-1735">
                From apparel to fleece, leather to boat covers, our laser
                etching services work on a variety of materials. Want to add a
                personalized touch that's as{' '}
                <span className="font-bold text-wh-pink" data-id="element-1736">
                  durable as it is beautiful
                </span>
                ? Our precision laser etching ensures your designs stand the
                test of time.
              </p>
            </div>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16" data-id="element-1737">
            {benefits.map((benefit, index) => <div key={index} className="bg-gray-50 rounded-xl p-8 border border-gray-100" data-id="element-1738">
                <div className="flex items-start gap-4" data-id="element-1739">
                  <div className="flex-shrink-0 w-12 h-12 bg-wh-pink rounded-lg flex items-center justify-center text-white" data-id="element-1740">
                    {benefit.icon}
                  </div>
                  <div data-id="element-1741">
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-1742">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600" data-id="element-1743">{benefit.description}</p>
                  </div>
                </div>
              </div>)}
          </div>

          {/* What We Can Etch */}
          <div className="mb-16" data-id="element-1744">
            <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1745">
              What We Can Etch
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1746">
              {materials.map((material, index) => <div key={index} className="bg-white rounded-xl p-8 border-2 border-gray-100 hover:border-wh-teal transition-colors" data-id="element-1747">
                  <div className="text-4xl mb-4" data-id="element-1748">{material.icon}</div>
                  <h3 className="text-2xl font-bold text-wh-teal mb-3" data-id="element-1749">
                    {material.title}
                  </h3>
                  <p className="text-gray-700" data-id="element-1750">{material.description}</p>
                </div>)}
            </div>
          </div>

          {/* Applications */}
          <div className="bg-gradient-to-br from-wh-teal to-teal-600 rounded-2xl p-12 text-white mb-16" data-id="element-1751">
            <h2 className="text-3xl font-bold mb-8 text-center" data-id="element-1752">
              Endless Possibilities
            </h2>
            <p className="text-xl text-white/90 text-center mb-8 max-w-3xl mx-auto" data-id="element-1753">
              Explore the world of laser etching, where your imagination takes
              center stage. Create unique patterns, logos, and text that stand
              out from the crowd.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto" data-id="element-1754">
              {applications.map((application, index) => <div key={index} className="flex items-center gap-2 bg-white/10 rounded-lg p-3" data-id="element-1755">
                  <CheckCircle className="w-5 h-5 text-wh-yellow flex-shrink-0" data-id="element-1756" />
                  <span className="text-sm font-medium" data-id="element-1757">{application}</span>
                </div>)}
            </div>
          </div>

          {/* Technology Highlight */}
          <div className="bg-gradient-to-br from-wh-pink to-pink-600 rounded-2xl p-12 mb-16" data-id="element-1758">
            <div className="max-w-3xl mx-auto text-center text-white" data-id="element-1759">
              <Zap className="w-16 h-16 mx-auto mb-6" data-id="element-1760" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1761">
                Seit Laser Bridge Technology
              </h2>
              <p className="text-xl text-white/90 mb-6" data-id="element-1762">
                Since 2009, our advanced Seit Laser Bridge has been the
                cornerstone of our laser etching capabilities. This precision
                equipment allows us to create intricate designs with microscopic
                accuracy, ensuring every detail is perfect.
              </p>
              <p className="text-lg text-white/90" data-id="element-1763">
                Whether you need custom logos, detailed artwork, or personalized
                text, our laser etching technology delivers professional results
                that last.
              </p>
            </div>
          </div>

          {/* CTA Box */}
          <div className="bg-white rounded-2xl p-12 text-center border-2 border-wh-yellow" data-id="element-1764">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-1765">
              Let's Make Your Vision a Reality
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-1766">
              Ready to transform your ideas into stunning laser-etched designs?
              From leather goods to custom apparel, we bring precision and
              artistry to every project.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1767">
              <a href="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-wh-pink text-white font-bold rounded-lg hover:bg-pink-600 transition-colors" data-id="element-1768">
                Get a Quote
              </a>
              <a href="/services" className="inline-flex items-center justify-center px-8 py-4 bg-wh-yellow text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors" data-id="element-1769">
                View All Services
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection data-id="element-1770" />
    </div>;
}